import { validateAgents } from './validate-agents';
import { validateBankDetails } from './validate-bank-details';
import { validateBusinessDocuments } from './validate-business-documents';
import { validateBusinessInfo } from './validate-business-info';
import { validateHeadOfficeAddress } from './validate-head-office-address';
import { validateOwnership } from './validate-ownership';
import { validatePointsOfSales } from './validate-points-of-sales';
import { validateRegisteredAddress } from './validate-registered-address';
import {
  ApplicationValidationErrors,
  ValidationError,
  ValidationErrorsMap,
} from './validation-error-types';

import { SectionName, sectionNames, StoreData } from '../../model';

export type ApplicationFormValidator = {
  section: (typeof sectionNames)[keyof typeof sectionNames];
  getErrors: () => ValidationError[] | Record<string, ValidationError[]>;
};

export function getValidators(
  formValues: Partial<
    Record<(typeof sectionNames)[keyof typeof sectionNames], unknown>
  >,
  shouldSkipEmpty: boolean
): Array<ApplicationFormValidator> {
  return [
    {
      section: sectionNames.ownership,
      getErrors: () => validateOwnership(formValues[sectionNames.ownership]),
    },
    {
      section: sectionNames.businessInfo,
      getErrors: () =>
        validateBusinessInfo(
          formValues[sectionNames.businessInfo],
          shouldSkipEmpty
        ),
    },
    {
      section: sectionNames.registeredAddress,
      getErrors: () =>
        validateRegisteredAddress(
          formValues[sectionNames.registeredAddress],
          shouldSkipEmpty
        ),
    },
    {
      section: sectionNames.headOfficeAddress,
      getErrors: () =>
        validateHeadOfficeAddress(
          formValues[sectionNames.headOfficeAddress],
          shouldSkipEmpty
        ),
    },
    {
      section: sectionNames.businessDocuments,
      getErrors: () =>
        validateBusinessDocuments(
          formValues[sectionNames.businessDocuments],
          shouldSkipEmpty
        ),
    },
    {
      section: sectionNames.bankDetails,
      getErrors: () =>
        validateBankDetails(
          formValues[sectionNames.bankDetails],
          shouldSkipEmpty
        ),
    },
    {
      section: sectionNames.pointsOfSales,
      getErrors: () =>
        validatePointsOfSales(
          formValues[sectionNames.pointsOfSales],
          shouldSkipEmpty
        ),
    },
    {
      section: sectionNames.agents,
      getErrors: () =>
        validateAgents(
          formValues[sectionNames.agents],
          {
            pointsOfSales: formValues[
              sectionNames.pointsOfSales
            ] as StoreData[],
          },
          shouldSkipEmpty
        ),
    },
  ];
}

const hasErrors = (result: ValidationError[] | ValidationErrorsMap): boolean =>
  Array.isArray(result) ? result.length > 0 : Object.keys(result).length > 0;

export function validateApplicationFormValues(
  formValues: Partial<Record<SectionName, unknown>>,
  shouldSkipEmpty: boolean
): ApplicationValidationErrors {
  const validators = getValidators(formValues, shouldSkipEmpty);

  return validators.reduce<ApplicationValidationErrors>(
    (errors, { section, getErrors }) => {
      const result = getErrors();
      if (hasErrors(result)) {
        errors[section] = result;
      }
      return errors;
    },
    {}
  );
}
